import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import {
  Graph,
  Node,
  Relationship,
  ResponseQuery,
  ResourceSection,
  Resource,
  Slice,
  SummaryType,
} from './APITypes';
import { Message, SearchFields } from '../state/ViewTypes';
import ProjectState from '../state/ViewTypes';
import type { FetchArgs } from '@reduxjs/toolkit/query';

// API endpoint for fetching data
export const graphApi = createApi({
  reducerPath: 'graphApi',
  baseQuery: retry(
    async (args: string | FetchArgs, api, extraOptions) => {
      const result = await fetchBaseQuery({
        baseUrl: process.env.REACT_APP_API_URL,
        prepareHeaders: (headers, api) => {
          let ai: any = api.getState();
          headers.set('Authorization', 'Bearer ' + ai.authReducer.accesstoken);
          return headers;
        },
      })(args, api, extraOptions);
      if (
        result.error &&
        result.error.originalStatus &&
        (result.error.originalStatus === 401 || // Dont believe the TS compiler, originalStatus can be there
          result.error.originalStatus === 403 ||
          result.error.originalStatus === 404)
      ) {
        retry.fail(result.error, result.meta);
      }
      return result;
    },
    { maxRetries: 3 },
  ),
  endpoints: (builder) => ({
    // <Type of data the call will return, Type of parameter being passed to the query function>
    getNode: builder.query<Node, string>({
      query: (id) => `node?id=${id}`,
    }),
    getSummary: builder.query<string, { id: string; type: SummaryType }>({
      query: ({ id, type }) => `node/summary/${type}?id=${id}`,
    }),
    getNeighbours: builder.query<Graph, { id: string; level: number }>({
      query: ({ id, level }) => `neighbours?id=${id}&level=${level}`,
    }),
    getRecommendedNeighbours: builder.query<
      { nodes: Array<Node>; distances: Array<number> },
      string
    >({
      query: (id) => `ai/neighbours?id=${id}`,
    }),
    getResources: builder.query<Array<Resource>, void>({
      query: () => `resources`,
    }),
    getResourceSections: builder.query<Array<ResourceSection>, string>({
      query: (id) => `resourceSections?id=${id}`,
    }),
    getResourceSectionDocs: builder.query<Graph, { id: string; level: number }>({
      query: ({ id, level }) => `resourceSectionDocs?id=${id}&level=${level}`,
    }),
    getResourceSummary: builder.query<Node, string>({
      query: (id) => `summary/resource?id=${id}`,
    }),
    search: builder.mutation<Slice<{ ids: Array<string> }>, SearchFields>({
      query: (body) => ({
        url: `search`,
        method: 'POST',
        body,
      }),
    }),
    AIsearch: builder.mutation<
      Slice<{ ids: Array<string>; distances: Array<number> }>,
      SearchFields
    >({
      query: (body) => ({ url: `ai/search`, method: 'POST', body }),
    }),

    getProjectState: builder.query<Array<{ id: string; json: ProjectState }>, string>({
      query: (q) => `project?psId=${q}`,
    }),
    getAIResponse: builder.mutation<Message, ResponseQuery>({
      query: (body) => ({
        url: `ai/respond`,
        method: 'POST',
        body,
      }),
    }),
    saveProjectState: builder.mutation<void, ProjectState>({
      query: (body) => ({
        url: `project`,
        method: 'POST',
        body,
      }),
    }),
    removeProjectState: builder.mutation<void, string>({
      query: (q) => ({
        url: `project?psId=${q}`,
        method: 'DELETE',
      }),
    }),
    getAllProjectStates: builder.query<Array<{ json: ProjectState; last_modified: string }>, void>({
      query: () => 'projects',
    }),
    getActiveProjectState: builder.query<Array<{ json: ProjectState }>, void>({
      query: () => 'project/active',
    }),
    setActiveProjectState: builder.mutation<void, string>({
      query: (body) => ({
        url: `project/active`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetNodeQuery, // X
  useLazyGetSummaryQuery,
  useGetSummaryQuery,
  useGetResourcesQuery, // X
  useGetResourceSectionsQuery, // X
  useGetResourceSectionDocsQuery, // X
  useSearchMutation,
  useAIsearchMutation,
  useSaveProjectStateMutation, // x
  useRemoveProjectStateMutation, // x
  useLazyGetAllProjectStatesQuery, // x
  useLazyGetActiveProjectStateQuery, // x
  useSetActiveProjectStateMutation, // x
  useLazyGetNeighboursQuery, // x
  useLazyGetRecommendedNeighboursQuery, // X
  useGetAIResponseMutation, // x
  useLazyGetNodeQuery,
} = graphApi;
